import { useState } from 'react'
import { Minus, Plus } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { DatePicker } from '@/components/ui/date-picker'
import { IM } from '@/components/store/inventory/itemMasterStrings'
import { cn } from '@/lib/utils'
import { useCurrency } from '@/contexts/CurrencyContext'
import { parsedQty, lineError, lineNeedsExpiry, linePriceProblem, type ReceiveDraftLine } from '../poReceiveDraft'
import { expectedLabel, stepReceivedQty, stepSizeFor } from './purchasingLogic'
import { parsePrice, receiptPriceAboveApproved, receivePriceChanged } from './purchasingPilotLogic'
import { PURCHASING_STRINGS, type PriceReason, type ReceiveReason } from './purchasingStrings'

const S = PURCHASING_STRINGS.receive

/** Kitchen flow lane D: one delivery line for staff who read little English.
 *  "Expected 10 kg → received [10]" pre-filled, big −/+ steppers, and — when
 *  what arrived differs — tap a reason chip instead of typing. Same
 *  ReceiveDraftLine and validation as the standard dialog, so the payload and
 *  the server rules are identical. */
export function PoReceiveSimpleLine({ line, onChange }: {
  line: ReceiveDraftLine & { packLabel?: string | null; packFactor?: number | null }
  onChange: (patch: Partial<ReceiveDraftLine>) => void
}) {
  const qty = parsedQty(line)
  const step = stepSizeFor(line.unit, line.open)
  const short = qty > 0 && qty < line.open - 0.0001
  const err = lineError(line)
  const [otherOpen, setOtherOpen] = useState(false)
  const named = line.reason !== 'Other' && (S.reasons as readonly string[]).includes(line.reason)
  const chip: ReceiveReason | null = named && !otherOpen ? (line.reason as ReceiveReason) : otherOpen || line.reason ? 'Other' : null
  const setReason = (r: ReceiveReason) => {
    setOtherOpen(r === 'Other')
    onChange({ reason: r === 'Other' ? '' : r })
  }
  // Pilot Batch 5: the delivery's price, pre-filled from the order. It is what
  // the stock is valued at and what the draft bill says.
  const { formatCurrency } = useCurrency()
  const priceProblem = linePriceProblem(line)
  const priceChanged = receivePriceChanged(line.unitCost, line.orderCost ?? null)
  const typedPrice = parsePrice(line.unitCost)
  const priceAbove = line.orderCost != null && typedPrice != null && receiptPriceAboveApproved(line.orderCost, typedPrice)
  const [priceOtherOpen, setPriceOtherOpen] = useState(false)
  const priceReason = line.priceReason ?? ''
  const namedPrice = priceReason !== 'Other' && (S.priceReasons as readonly string[]).includes(priceReason)
  const priceChip: PriceReason | null = namedPrice && !priceOtherOpen ? (priceReason as PriceReason) : priceOtherOpen || priceReason ? 'Other' : null
  const setPriceReason = (r: PriceReason) => {
    setPriceOtherOpen(r === 'Other')
    onChange({ priceReason: r === 'Other' ? '' : r })
  }

  return (
    <div className={cn('rounded-lg border-2 p-4 space-y-3', short ? 'border-warning-border' : 'border-border')}>
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <span className="text-base font-semibold">{line.itemName}</span>
        <span className="text-sm text-muted-foreground tabular-nums">
          {S.expected} <span className="font-semibold text-foreground">{expectedLabel(line.open, line.unit, line.packLabel, line.packFactor)}</span>
        </span>
      </div>
      <div className="flex items-center gap-2">
        <span className="w-20 text-sm font-medium">{S.received}</span>
        <Button type="button" variant="outline" className="h-10 w-10 p-0" aria-label="Less"
          onClick={() => onChange({ qty: String(stepReceivedQty(qty, -step, line.open)) })}>
          <Minus className="h-4 w-4" />
        </Button>
        <Input
          inputMode="decimal" aria-label={`${S.received} ${line.itemName}`}
          className="w-24 text-center font-semibold tabular-nums"
          value={line.qty} onChange={(e) => onChange({ qty: e.target.value })}
        />
        <Button type="button" variant="outline" className="h-10 w-10 p-0" aria-label="More"
          disabled={qty >= line.open - 0.0001}
          onClick={() => onChange({ qty: String(stepReceivedQty(qty, step, line.open)) })}>
          <Plus className="h-4 w-4" />
        </Button>
        <span className="text-sm text-muted-foreground">{line.unit}</span>
      </div>
      {/* Lane A: batch details for FEFO and recalls — optional, but the
          expiry is what puts the right batch first on the issue screen. */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <label className="space-y-1">
          <span className="text-sm font-medium">
            {line.tracksExpiry ? IM.lots.expiryRequiredLabel : IM.lots.expiryLabel}
            {line.tracksExpiry && <span className="ml-0.5 text-red-500">*</span>}
          </span>
          <DatePicker value={line.expiry} onChange={(v) => onChange({ expiry: v })} />
          {lineNeedsExpiry(line) && <span className="block text-xs text-danger-fg">{IM.lots.expiryNeeded}</span>}
        </label>
        <label className="space-y-1">
          <span className="text-sm font-medium">{IM.lots.lotNoLabel}</span>
          <Input maxLength={64} placeholder={IM.lots.lotNoPlaceholder}
            value={line.lotNo ?? ''} onChange={(e) => onChange({ lotNo: e.target.value })} />
        </label>
      </div>
      <div className="space-y-2">
        <div className="flex flex-wrap items-end gap-3">
          <label className="space-y-1">
            <span className="text-sm font-medium">{S.price(line.unit)}</span>
            <Input
              inputMode="decimal" className="w-32 tabular-nums" aria-label={`${S.price(line.unit)} ${line.itemName}`}
              value={line.unitCost}
              onChange={(e) => onChange({ unitCost: e.target.value, noPrice: false })}
            />
          </label>
          <span className="pb-2 text-sm text-muted-foreground">
            {line.orderCost != null ? S.orderPrice(formatCurrency(line.orderCost)) : S.noOrderPrice}
          </span>
          {priceChanged && (
            <span className="mb-2 rounded-full border border-warning-border bg-warning-surface px-2 py-0.5 text-xs font-medium text-warning-fg">
              {S.priceChanged}
            </span>
          )}
        </div>
        {line.orderCost == null && !line.unitCost.trim() && qty > 0 && (
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" className="h-4 w-4" checked={Boolean(line.noPrice)} onChange={(e) => onChange({ noPrice: e.target.checked })} />
            {S.noPrice}
          </label>
        )}
        {priceProblem === 'needs_price' && <p className="text-sm text-danger-fg">{S.needsPrice}</p>}
        {priceProblem === 'bad_price' && <p className="text-sm text-danger-fg">{S.badPrice}</p>}
        {priceAbove && qty > 0 && (
          <div className="space-y-2">
            <p className={cn('text-sm font-medium', priceProblem === 'needs_price_reason' && 'text-warning-fg')}>{S.whyPrice}</p>
            <div className="flex flex-wrap gap-2">
              {S.priceReasons.map((r) => (
                <button
                  key={r} type="button" aria-pressed={priceChip === r}
                  onClick={() => setPriceReason(r)}
                  className={cn(
                    'h-9 rounded-full border px-3 text-sm font-medium',
                    priceChip === r ? 'border-primary bg-primary text-primary-foreground' : 'border-border hover:bg-muted/40',
                  )}
                >
                  {r}
                </button>
              ))}
            </div>
            {priceChip === 'Other' && (
              <Input placeholder={S.pricePlaceholder} value={priceReason}
                onChange={(e) => onChange({ priceReason: e.target.value })} />
            )}
          </div>
        )}
      </div>
      {err === 'over_receipt' && (
        <p className="text-sm font-medium text-danger-fg">More than expected — record the extra as a Quick purchase.</p>
      )}
      {short && (
        <div className="space-y-2">
          <p className={cn('text-sm font-medium', err === 'needs_reason' && 'text-warning-fg')}>
            {S.lessArrived(String(qty), String(line.open))} — {S.whyTitle}
          </p>
          <div className="flex flex-wrap gap-2">
            {S.reasons.map((r) => (
              <button
                key={r} type="button" aria-pressed={chip === r}
                onClick={() => setReason(r)}
                className={cn(
                  'h-9 rounded-full border px-3 text-sm font-medium',
                  chip === r ? 'border-primary bg-primary text-primary-foreground' : 'border-border hover:bg-muted/40',
                )}
              >
                {r}
              </button>
            ))}
          </div>
          {chip === 'Other' && (
            <Input placeholder={S.otherPlaceholder} value={line.reason}
              onChange={(e) => onChange({ reason: e.target.value })} />
          )}
        </div>
      )}
    </div>
  )
}
